import type {
    ChangeEvent,
    FC,
    FormEvent,
    ReactNode
} from 'react'
import type { ConsortiumSlug, SimulationSelection } from '@/types'
import type { LeadErrors, LeadField } from '@/lib/lead-validation'

import { useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { cn } from '@/lib/utils'
import { simulateConsortium } from '@/lib/consortium'
import { cleanSourcePage, LEAD_MESSAGE_MAX_LENGTH, validateLead } from '@/lib/lead-validation'
import { buttonClassName } from '@/components/generals/button'
import { consortiumCategories, getConsortiumCategory } from '@/constants/consorcios'
import { LEAD_FORM_FIRST_FIELD_ID } from '@/hooks/use-proposal-selection'

import {
    ArrowRight,
    ChevronDown,
    CircleAlert,
    CircleCheckBig,
    LoaderCircle,
    TriangleAlert,
    X
} from 'lucide-react'

import {
    formatCurrency,
    formatNumber,
    maskCurrencyInput,
    maskPhone,
    onlyDigits
} from '@/lib/format'

interface LeadFormProps {
    defaultSlug?: ConsortiumSlug
    selection: SimulationSelection | null
    onClearSelection: () => void
}

interface FieldProps {
    id: string
    label: string
    hint?: string
    error?: string
    children: ReactNode
}

interface FormValues {
    name: string
    phone: string
    email: string
    category: string
    credit: string
    message: string
    consent: boolean
    website: string
}

interface LeadResponse {
    ok?: boolean
    message?: string
    errors?: LeadErrors
}

type FormStatus = 'idle' | 'submitting' | 'success' | 'error'

const fieldOrder: LeadField[] = ['name', 'phone', 'email', 'category', 'creditValue', 'message', 'consent']

const fieldIds: Record<LeadField, string> = {
    name: LEAD_FORM_FIRST_FIELD_ID,
    phone: 'proposta-whatsapp',
    email: 'proposta-email',
    category: 'proposta-tipo',
    creditValue: 'proposta-valor',
    message: 'proposta-mensagem',
    consent: 'proposta-aceite'
}

const inputClassName = 'mt-2 block h-12 w-full rounded-xl border border-input bg-card px-4 text-base text-foreground shadow-xs transition-[color,border-color,box-shadow] placeholder:text-muted-foreground/70 hover:border-foreground/45 focus-visible:border-ring focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-ring/20 aria-[invalid=true]:border-destructive aria-[invalid=true]:ring-destructive/15'

const createInitialValues = (defaultSlug?: ConsortiumSlug): FormValues => ({
    name: '',
    phone: '',
    email: '',
    category: defaultSlug ?? '',
    credit: '',
    message: '',
    consent: false,
    website: ''
})

const errorId = (field: LeadField): string => `${fieldIds[field]}-erro`

const Field: FC<FieldProps> = ({ id, label, hint, error, children }): ReactNode => {
    return (
        <div>
            <div className='flex items-baseline justify-between gap-3'>
                <label htmlFor={id} className='text-sm font-semibold text-foreground'>
                    {label}
                </label>

                {hint && (
                    <span className='text-xs text-muted-foreground'>
                        {hint}
                    </span>
                )}
            </div>

            {children}

            {error && (
                <p id={`${id}-erro`} className='mt-2 flex items-center gap-1.5 text-sm font-medium text-destructive'>
                    <CircleAlert aria-hidden='true' className='size-4 shrink-0' />
                    {error}
                </p>
            )}
        </div>
    )
}

export const LeadForm: FC<LeadFormProps> = ({ defaultSlug, selection, onClearSelection }): ReactNode => {
    const router = useRouter()
    const successRef = useRef<HTMLDivElement>(null)
    const [values, setValues] = useState<FormValues>(() => createInitialValues(defaultSlug))
    const [errors, setErrors] = useState<LeadErrors>({})
    const [status, setStatus] = useState<FormStatus>('idle')
    const [serverMessage, setServerMessage] = useState('')

    useEffect(() => {
        if (!selection) return

        setValues(current => ({
            ...current,
            category: selection.slug,
            credit: formatNumber(selection.credit)
        }))

        setErrors(current => ({
            ...current,
            category: undefined,
            creditValue: undefined
        }))

        setStatus(current => current === 'success' ? 'idle' : current)
    }, [selection])

    useEffect(() => {
        if (status === 'success') successRef.current?.focus()
    }, [status])

    const selectedCategory = getConsortiumCategory(values.category)
    const selectionCategory = selection ? getConsortiumCategory(selection.slug) : undefined
    const selectionSimulation = selection && selectionCategory ? simulateConsortium(selectionCategory, selection.credit, selection.term) : null

    const clearError = (field: LeadField): void => {
        setErrors(current => current[field] ? { ...current, [field]: undefined } : current)
    }

    const fieldA11y = (field: LeadField) => ({
        'aria-invalid': errors[field] ? true : undefined,
        'aria-describedby': errors[field] ? errorId(field) : undefined
    })

    const onNameChange = (event: ChangeEvent<HTMLInputElement>): void => {
        const { value } = event.target

        setValues(current => ({ ...current, name: value }))
        clearError('name')
    }

    const onPhoneChange = (event: ChangeEvent<HTMLInputElement>): void => {
        const value = maskPhone(event.target.value)

        setValues(current => ({ ...current, phone: value }))
        clearError('phone')
    }

    const onEmailChange = (event: ChangeEvent<HTMLInputElement>): void => {
        const { value } = event.target

        setValues(current => ({ ...current, email: value }))
        clearError('email')
    }

    const onCategoryChange = (event: ChangeEvent<HTMLSelectElement>): void => {
        const { value } = event.target

        setValues(current => ({ ...current, category: value }))
        clearError('category')

        if (selection && value !== selection.slug) onClearSelection()
    }

    const onCreditChange = (event: ChangeEvent<HTMLInputElement>): void => {
        const value = maskCurrencyInput(event.target.value)

        setValues(current => ({ ...current, credit: value }))
        clearError('creditValue')

        if (selection && Number(onlyDigits(value)) !== selection.credit) onClearSelection()
    }

    const onMessageChange = (event: ChangeEvent<HTMLTextAreaElement>): void => {
        const { value } = event.target

        setValues(current => ({ ...current, message: value }))
        clearError('message')
    }

    const onConsentChange = (event: ChangeEvent<HTMLInputElement>): void => {
        const { checked } = event.target

        setValues(current => ({ ...current, consent: checked }))
        clearError('consent')
    }

    const onWebsiteChange = (event: ChangeEvent<HTMLInputElement>): void => {
        const { value } = event.target

        setValues(current => ({ ...current, website: value }))
    }

    const onSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault()

        if (status === 'submitting') return

        const payload = {
            name: values.name,
            phone: values.phone,
            email: values.email,
            category: values.category,
            creditValue: values.credit ? Number(onlyDigits(values.credit)) : null,
            termMonths: selection && selection.slug === values.category ? selection.term : null,
            message: values.message,
            consent: values.consent,
            sourcePage: cleanSourcePage(router.asPath),
            website: values.website
        }

        const validation = validateLead(payload)

        if (!validation.data) {
            setErrors(validation.errors)
            setServerMessage('')
            setStatus('idle')

            const firstInvalid = fieldOrder.find(field => validation.errors[field])

            if (firstInvalid) document.getElementById(fieldIds[firstInvalid])?.focus()

            return
        }

        setStatus('submitting')
        setServerMessage('')

        try {
            const response = await fetch('/api/leads', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            })

            const data = await response.json().catch(() => null) as LeadResponse | null

            if (response.ok && data?.ok) {
                setErrors({})
                setStatus('success')
                onClearSelection()

                setValues(current => ({
                    ...createInitialValues(defaultSlug),
                    name: current.name,
                    phone: current.phone,
                    email: current.email
                }))

                return
            }

            if (data?.errors) setErrors(data.errors)

            setServerMessage(data?.message ?? 'Não conseguimos enviar agora. Tente novamente em instantes.')
            setStatus('error')
        }
        catch {
            setServerMessage('Não foi possível conectar. Confira sua internet e tente de novo.')
            setStatus('error')
        }
    }

    if (status === 'success') {
        const firstName = values.name.trim().split(' ')[0]

        return (
            <div ref={successRef} tabIndex={-1} role='status' className='flex flex-col items-start py-4 outline-none'>
                <span className='grid size-14 place-items-center rounded-2xl bg-gold text-gold-foreground'>
                    <CircleCheckBig aria-hidden='true' className='size-7' />
                </span>

                <h3 className='mt-7 font-heading text-4xl leading-tight text-foreground sm:text-5xl'>
                    Pedido recebido{firstName ? `, ${firstName}` : ''}.
                </h3>

                <p className='mt-4 max-w-md text-lg leading-relaxed text-muted-foreground'>
                    Um especialista vai analisar os grupos disponíveis e falar com você pelo WhatsApp ou por e-mail com a proposta.
                </p>

                <button
                    type='button'
                    onClick={() => setStatus('idle')}
                    className={buttonClassName({ variant: 'outline', className: 'mt-9' })}
                >
                    Fazer outro pedido
                </button>
            </div>
        )
    }

    return (
        <form noValidate onSubmit={onSubmit} className='relative grid gap-5'>
            {selection && selectionCategory && selectionSimulation && (
                <div className='flex items-start justify-between gap-4 rounded-2xl border border-gold/60 bg-gold/10 p-4'>
                    <div>
                        <p className='text-xs font-bold uppercase tracking-[0.16em] text-bronze'>
                            Simulação escolhida
                        </p>

                        <p className='mt-1.5 font-semibold text-foreground'>
                            {selectionCategory.title}: {formatCurrency(selection.credit)} em {selection.term} meses
                        </p>

                        <p className='mt-0.5 text-sm text-muted-foreground'>
                            Parcela estimada de {formatCurrency(selectionSimulation.installment, { cents: true })} por mês
                        </p>
                    </div>

                    <button
                        type='button'
                        onClick={onClearSelection}
                        className='grid size-9 shrink-0 cursor-pointer place-items-center rounded-full text-foreground transition-colors hover:bg-gold/25 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring'
                    >
                        <X aria-hidden='true' className='size-4' />
                        <span className='sr-only'>
                            Remover simulação
                        </span>
                    </button>
                </div>
            )}

            <Field id={fieldIds.name} label='Nome completo' error={errors.name}>
                <input
                    id={fieldIds.name}
                    name='name'
                    type='text'
                    autoComplete='name'
                    required
                    value={values.name}
                    onChange={onNameChange}
                    className={inputClassName}
                    {...fieldA11y('name')}
                />
            </Field>

            <div className='grid gap-5 sm:grid-cols-2'>
                <Field id={fieldIds.phone} label='WhatsApp' error={errors.phone}>
                    <input
                        id={fieldIds.phone}
                        name='phone'
                        type='tel'
                        inputMode='tel'
                        autoComplete='tel-national'
                        placeholder='(11) 91234-5678'
                        required
                        value={values.phone}
                        onChange={onPhoneChange}
                        className={inputClassName}
                        {...fieldA11y('phone')}
                    />
                </Field>

                <Field id={fieldIds.email} label='E-mail' error={errors.email}>
                    <input
                        id={fieldIds.email}
                        name='email'
                        type='email'
                        inputMode='email'
                        autoComplete='email'
                        placeholder='voce@email.com'
                        required
                        value={values.email}
                        onChange={onEmailChange}
                        className={inputClassName}
                        {...fieldA11y('email')}
                    />
                </Field>
            </div>

            <div className='grid gap-5 sm:grid-cols-2'>
                <Field id={fieldIds.category} label='Tipo de consórcio' error={errors.category}>
                    <div className='relative'>
                        <select
                            id={fieldIds.category}
                            name='category'
                            required
                            value={values.category}
                            onChange={onCategoryChange}
                            className={cn(inputClassName, 'cursor-pointer appearance-none pr-11')}
                            {...fieldA11y('category')}
                        >
                            <option value='' disabled>
                                Selecione
                            </option>

                            {consortiumCategories.map(category => (
                                <option key={category.slug} value={category.slug}>
                                    {category.name}
                                </option>
                            ))}
                        </select>

                        <ChevronDown
                            aria-hidden='true'
                            className='pointer-events-none absolute right-4 top-1/2 mt-1 size-5 -translate-y-1/2 text-muted-foreground'
                        />
                    </div>
                </Field>

                <Field id={fieldIds.creditValue} label='Valor do crédito' hint='Opcional' error={errors.creditValue}>
                    <div className='relative'>
                        <span
                            aria-hidden='true'
                            className='pointer-events-none absolute left-4 top-1/2 mt-1 -translate-y-1/2 text-base font-semibold text-muted-foreground'
                        >
                            R$
                        </span>

                        <input
                            id={fieldIds.creditValue}
                            name='creditValue'
                            type='text'
                            inputMode='numeric'
                            autoComplete='off'
                            placeholder={formatNumber(selectedCategory?.credit.initial ?? 300000)}
                            value={values.credit}
                            onChange={onCreditChange}
                            className={cn(inputClassName, 'pl-12 tabular-nums')}
                            {...fieldA11y('creditValue')}
                        />
                    </div>
                </Field>
            </div>

            <Field id={fieldIds.message} label='Mensagem' hint='Opcional' error={errors.message}>
                <textarea
                    id={fieldIds.message}
                    name='message'
                    rows={4}
                    maxLength={LEAD_MESSAGE_MAX_LENGTH}
                    placeholder='Conte o que você quer conquistar e em quanto tempo.'
                    value={values.message}
                    onChange={onMessageChange}
                    className={cn(inputClassName, 'h-auto min-h-28 resize-y py-3 leading-relaxed')}
                    {...fieldA11y('message')}
                />
            </Field>

            <div aria-hidden='true' className='absolute -left-[9999px] top-0 size-px overflow-hidden'>
                <label htmlFor='proposta-site'>
                    Deixe este campo em branco
                </label>

                <input
                    id='proposta-site'
                    name='website'
                    type='text'
                    tabIndex={-1}
                    autoComplete='off'
                    value={values.website}
                    onChange={onWebsiteChange}
                />
            </div>

            <div>
                <div className='flex items-start gap-3'>
                    <input
                        id={fieldIds.consent}
                        name='consent'
                        type='checkbox'
                        required
                        checked={values.consent}
                        onChange={onConsentChange}
                        className='mt-0.5 size-5 shrink-0 cursor-pointer rounded accent-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring'
                        {...fieldA11y('consent')}
                    />

                    <label htmlFor={fieldIds.consent} className='cursor-pointer text-sm leading-relaxed text-muted-foreground'>
                        Li e aceito a{' '}
                        <Link
                            href='/privacidade'
                            target='_blank'
                            className='rounded font-semibold text-foreground underline underline-offset-4 hover:text-bronze focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring'
                        >
                            política de privacidade
                        </Link>
                        {' '}e autorizo o contato pelo WhatsApp, telefone ou e-mail para receber a proposta.
                    </label>
                </div>

                {errors.consent && (
                    <p id={errorId('consent')} className='mt-2 flex items-center gap-1.5 text-sm font-medium text-destructive'>
                        <CircleAlert aria-hidden='true' className='size-4 shrink-0' />
                        {errors.consent}
                    </p>
                )}
            </div>

            {status === 'error' && serverMessage && (
                <div role='alert' className='flex items-start gap-3 rounded-xl border border-destructive/30 bg-destructive/5 p-4 text-sm font-medium text-destructive'>
                    <TriangleAlert aria-hidden='true' className='mt-0.5 size-5 shrink-0' />
                    <p>
                        {serverMessage}
                    </p>
                </div>
            )}

            <button
                type='submit'
                disabled={status === 'submitting'}
                className={buttonClassName({ size: 'lg', className: 'mt-1 w-full' })}
            >
                {status === 'submitting' ? (
                    <>
                        <LoaderCircle aria-hidden='true' className='size-5 animate-spin' />
                        Enviando seu pedido
                    </>
                ) : (
                    <>
                        Receber minha proposta
                        <ArrowRight aria-hidden='true' className='size-5' />
                    </>
                )}
            </button>

            <p className='text-center text-xs leading-relaxed text-muted-foreground'>
                Sem custo e sem compromisso. Seus dados são usados apenas para preparar a sua proposta.
            </p>
        </form>
    )
}
